import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Mail,
  Phone,
  Car,
  Trash2,
  Paperclip,
  Loader2,
  MailOpen,
  CircleDot,
  Wrench,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api, API, TOKEN_KEY } from "@/lib/api";

export default function MessagesPanel({ onChange }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/messages");
      setMessages(data.messages);
    } catch {
      toast.error("Eroare la încărcarea mesajelor");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleRead = async (m, forceRead) => {
    const is_read = forceRead !== undefined ? forceRead : !m.is_read;
    await api.patch(`/admin/messages/${m.id}/read`, { is_read });
    setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, is_read } : x)));
    onChange?.();
  };

  const remove = async (id) => {
    await api.delete(`/admin/messages/${id}`);
    setMessages((ms) => ms.filter((x) => x.id !== id));
    onChange?.();
    toast.success("Mesaj șters");
  };

  const openExpand = (m) => {
    const next = expanded === m.id ? null : m.id;
    setExpanded(next);
    if (next && !m.is_read) toggleRead(m, true);
  };

  const attachmentUrl = (mId, idx) =>
    `${API}/admin/messages/${mId}/attachment/${idx}?auth=${localStorage.getItem(TOKEN_KEY)}`;

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("ro-RO", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-20 text-slate-400">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  if (!messages.length)
    return (
      <div className="bg-white rounded-2xl border border-[#E2E8F0] p-16 text-center">
        <Mail className="w-12 h-12 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium">Niciun mesaj deocamdată.</p>
      </div>
    );

  return (
    <div className="space-y-3">
      {messages.map((m) => (
        <div
          key={m.id}
          data-testid={`admin-message-row-${m.id}`}
          className={`bg-white rounded-xl border transition-colors duration-200 ${
            m.is_read ? "border-[#E2E8F0]" : "border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.02]"
          }`}
        >
          <button
            onClick={() => openExpand(m)}
            data-testid="admin-message-row-toggle"
            className="w-full text-left p-4 sm:p-5 flex items-start gap-4"
          >
            <span className="shrink-0 mt-1">
              {m.is_read ? (
                <MailOpen className="w-5 h-5 text-slate-400" />
              ) : (
                <CircleDot className="w-5 h-5 text-[#FF5A1F]" />
              )}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`font-semibold ${m.is_read ? "text-[#0F1E36]" : "text-[#0F1E36]"}`}>
                  {m.nume}
                </span>
                {!m.is_read && (
                  <Badge className="bg-[#FF5A1F] text-white text-[10px]">NOU</Badge>
                )}
                {m.serviciu && (
                  <Badge variant="outline" className="text-[10px] border-[#E2E8F0] text-slate-500">
                    {m.serviciu}
                  </Badge>
                )}
                {m.attachments?.length > 0 && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                    <Paperclip className="w-3 h-3" />
                    {m.attachments.length}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 truncate mt-1">{m.mesaj}</p>
            </div>
            <span className="shrink-0 text-xs text-slate-400 font-mono">
              {fmtDate(m.created_at)}
            </span>
          </button>

          {expanded === m.id && (
            <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-[#E2E8F0] space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-4">
                <Detail icon={Phone} label="Telefon">
                  <a href={`tel:${m.telefon}`} className="text-[#FF5A1F] font-mono">
                    {m.telefon}
                  </a>
                </Detail>
                <Detail icon={Mail} label="Email">
                  <a href={`mailto:${m.email}`} className="text-[#FF5A1F]">
                    {m.email}
                  </a>
                </Detail>
                {m.vehicul && (
                  <Detail icon={Car} label="Vehicul">
                    {m.vehicul}
                  </Detail>
                )}
                {m.serviciu && (
                  <Detail icon={Wrench} label="Serviciu solicitat">
                    {m.serviciu}
                  </Detail>
                )}
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-1">
                  Mesaj
                </p>
                <p className="text-sm text-[#0F1E36] leading-relaxed whitespace-pre-wrap">
                  {m.mesaj}
                </p>
              </div>

              {m.attachments?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-2">
                    Atașamente
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {m.attachments.map((a, idx) => (
                      <a
                        key={idx}
                        href={attachmentUrl(m.id, idx)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`admin-attachment-${m.id}-${idx}`}
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F4F6F9] border border-[#E2E8F0] text-sm text-[#0F1E36] hover:border-[#FF5A1F] transition-colors duration-200"
                      >
                        <Paperclip className="w-4 h-4 text-[#FF5A1F]" />
                        <span className="truncate max-w-[180px]">{a.filename}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => toggleRead(m)}
                  className="border-[#E2E8F0]"
                >
                  {m.is_read ? "Marchează necitit" : "Marchează citit"}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid="admin-message-delete-button"
                      className="border-[#FEE2E2] text-[#DC2626] hover:bg-[#FEE2E2]"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" /> Șterge
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Ștergi acest mesaj?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Acțiunea este definitivă și nu poate fi anulată.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Anulează</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => remove(m.id)}
                        className="bg-[#DC2626] hover:bg-[#B91C1C]"
                      >
                        Șterge
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function Detail({ icon: Icon, label, children }) {
  return (
    <div className="flex items-center gap-2.5">
      <Icon className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="text-sm">
        <span className="text-slate-400">{label}: </span>
        <span className="text-[#0F1E36] font-medium">{children}</span>
      </div>
    </div>
  );
}
